/**
 * <mb-group-shop>: "compre junto" (portado de components/groupShop.js + a parte de variantes do
 * productPurchase.js usada pelos blocos do conjunto).
 * - Todos os itens entram selecionados no carregamento, como no tema anterior; o "x" tira/põe cada um.
 * - Cada item tem sua própria régua de atributos: trocar a variante atualiza o preço do item e o total.
 * - "COMPRAR JUNTO" envia os itens disponíveis de uma vez em POST /cart/add.js.
 * - Com `data-tag-url`, os outros produtos vêm da tag `compre-junto-*` (como no site atual): os 3 primeiros
 *   da tag, sem o próprio produto, buscados em /collections/all/<tag>?section_id=vitrine-tag. O bloco só
 *   aparece se a tag tiver pelo menos 2 produtos, a regra do site atual (`products_count >= 2`).
 */
const { emit, formatMoney, updatePriceBlock, withUsdPrice } = window.MB;

class MbGroupShop extends HTMLElement {
  connectedCallback() {
    if (this.dataset.tagUrl) this.load();
    else this.init();
  }

  async load() {
    try {
      const response = await fetch(this.dataset.tagUrl);
      if (!response.ok) throw new Error(String(response.status));
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const loaded = [...doc.querySelectorAll('[data-vitrine-tag="group"] [data-vitrine-item]')];
      const others = loaded.filter((item) => item.dataset.vitrineItem !== this.dataset.productId);
      if (loaded.length < 2 || !others.length) {
        this.remove();
        return;
      }
      const equal = this.querySelector('.symbol.equal');
      others.forEach((item) => {
        const plus = document.createElement('div');
        plus.className = 'symbol';
        plus.textContent = '+';
        equal.before(plus, ...item.children);
      });
      this.hidden = false;
      this.init();
    } catch (error) {
      this.remove();
    }
  }

  init() {
    this.items = Array.from(this.querySelectorAll('[data-prod-group-shop]'));
    if (!this.items.length) return;
    this.purchase = this.querySelector('[data-group-shop-purchase]');
    this.total = this.querySelector('[data-group-total]');
    this.original = this.querySelector('[data-group-original]');
    this.discount = this.querySelector('[data-group-discount]');
    this.warning = this.querySelector('[data-warning]');
    this.error = this.querySelector('[data-msg-error]');
    this.button = this.querySelector('[data-group-shop-add]');
    this.selected = new Set();

    this.items.forEach((item) => {
      item.variants = this.parseVariants(item);
      item.optionGroups = Array.from(item.querySelectorAll('[data-prod-option]'));
      item.optionGroups.forEach((group) => {
        group.querySelectorAll('input[type="radio"]').forEach((input) => {
          input.addEventListener('change', () => this.onVariantChange(item));
        });
      });
      this.markAvailability(item);

      const toggle = item.querySelector('.add-to-group');
      if (toggle) {
        toggle.addEventListener('click', () => this.toggle(item));
      }
      // O tema anterior começava com todos os produtos do conjunto marcados.
      this.select(item);
    });

    if (this.button) this.button.addEventListener('click', () => this.addAll());
    this.updateTotal();
  }

  parseVariants(item) {
    const json = item.querySelector('[data-variants]');
    if (!json) return [];
    try {
      return JSON.parse(json.textContent);
    } catch (error) {
      return [];
    }
  }

  selectedValues(item) {
    return item.optionGroups.map((group) => {
      const checked = group.querySelector('input[type="radio"]:checked');
      return checked ? checked.value : null;
    });
  }

  markAvailability(item) {
    if (!item.variants.length) return;
    const selected = this.selectedValues(item);
    item.optionGroups.forEach((group, index) => {
      group.querySelectorAll('label[data-attribute-value]').forEach((label) => {
        const candidate = [...selected];
        candidate[index] = label.dataset.attributeValue;
        const matches = item.variants.filter((variant) => variant.options.every((value, i) => candidate[i] === null || candidate[i] === value));
        label.classList.toggle('-disabled', matches.length === 0);
        label.classList.toggle('-unavailable', matches.length > 0 && !matches.some((variant) => variant.available));
      });
    });
  }

  onVariantChange(item) {
    this.markAvailability(item);
    const values = this.selectedValues(item);
    const variant = item.variants.find((candidate) => candidate.options.every((value, index) => values[index] === null || values[index] === value));
    if (!variant) return;
    item.dataset.variantId = variant.id;
    item.dataset.price = variant.compare_at_price || variant.price;
    item.dataset.salePrice = variant.price;
    item.dataset.available = String(Boolean(variant.available));
    updatePriceBlock(item.querySelector('[data-price-block]'), variant);
    this.updateTotal();
  }

  toggle(item) {
    if (this.selected.has(item)) this.unselect(item);
    else this.select(item);
    this.updateTotal();
  }

  select(item) {
    this.selected.add(item);
    const toggle = item.querySelector('.add-to-group');
    if (toggle) toggle.classList.add('-group-selected');
  }

  unselect(item) {
    this.selected.delete(item);
    const toggle = item.querySelector('.add-to-group');
    if (toggle) toggle.classList.remove('-group-selected');
  }

  updateTotal() {
    if (!this.purchase) return;
    if (!this.selected.size) {
      this.purchase.classList.add('-hidden');
      return;
    }
    this.purchase.classList.remove('-hidden');

    let full = 0;
    let sale = 0;
    let unavailable = 0;
    this.selected.forEach((item) => {
      if (item.dataset.available === 'false') {
        unavailable += 1;
        return;
      }
      full += Number(item.dataset.price) || 0;
      sale += Number(item.dataset.salePrice) || 0;
    });

    if (this.total) this.total.textContent = formatMoney(sale);
    if (this.discount) this.discount.classList.toggle('-hidden', full <= sale);
    if (this.original) this.original.textContent = full > sale ? formatMoney(full) : '';
    if (this.warning) {
      const strings = (window.theme && window.theme.strings) || {};
      const template = unavailable === 1 ? strings.groupShopUnavailableOne : strings.groupShopUnavailableMany;
      this.warning.textContent = unavailable > 0 && template ? template.replace('[count]', unavailable) : '';
    }
  }

  showError(message) {
    if (!this.error) return;
    this.error.textContent = message || '';
    this.error.classList.toggle('-visible', Boolean(message));
  }

  async addAll() {
    const items = [];
    this.selected.forEach((item) => {
      if (item.dataset.available === 'false') return;
      items.push(withUsdPrice({ id: Number(item.dataset.variantId), quantity: 1 }, Number(item.dataset.salePrice)));
    });
    if (!items.length) return;

    this.showError('');
    this.button.classList.add('-adding');
    try {
      const response = await fetch(this.dataset.cartAdd, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message || 'error');
      emit('mb:cart:updated', { items: data.items, open: true });
    } catch (error) {
      this.showError((window.theme && window.theme.strings && window.theme.strings.addError) || error.message);
    }
    this.button.classList.remove('-adding');
  }
}

if (!customElements.get('mb-group-shop')) customElements.define('mb-group-shop', MbGroupShop);
